import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/contact.css";
import { Phone, Mail, MapPin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa6";
import UseSEO from "../../Hooks/useSEO";
import Swal from "sweetalert2";
import localBusinessSchema from "../../SEO/Schemas/localBusinessSchema";

const Contact = () => {

    // ✅ SEO – unchanged
    UseSEO({
        title:
            "Sujaramaha Enterprises | Contact | Computer & Printers Sales & Services, CCTV, Insurance",
        description:
            "Contact Sujaramaha Enterprises for computer & printer services, CCTV installation, insurance assistance, water filters, solar heaters, inverter & battery solutions.",
        keywords:
            "computer service, printer service, CCTV installation, insurance assistance, Sujaramaha Enterprises",
        jsonLd: localBusinessSchema
    });

    const [result, setResult] = useState("");
    const [errors, setErrors] = useState({});

    const onSubmit = async (event) => {
        event.preventDefault();

        const form = event.target;
        const formData = new FormData(form);

        // 🛡️ HONEYPOT CHECK (FREE – bots only)
        const honeypot = formData.get("company");
        if (honeypot) return;

        const name = formData.get("name")?.trim();
        const tel = formData.get("tel")?.trim();
        const email = formData.get("email")?.trim();
        const message = formData.get("message")?.trim();
        const terms = formData.get("terms");

        const newErrors = {};

        if (!name) newErrors.name = "Full name is required";
        if (!tel) newErrors.tel = "Mobile number is required";
        else if (!/^[6-9]\d{9}$/.test(tel))
            newErrors.tel = "Enter valid 10-digit mobile number";
        if (!email) newErrors.email = "Email is required";
        if (!message) newErrors.message = "Message is required";
        if (!terms) newErrors.terms = "Please accept Terms & Conditions";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setResult("Sending...");

        // 🔐 Web3Forms access key
        formData.append("access_key", "49bc87b8-dd3d-4e30-9774-6b1bb9a0da94");

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                mode: "cors",
                body: formData
            });

            const data = await response.json();
            if (!data.success) throw new Error(data.message);

            Swal.fire({
                icon: "success",
                title: "Message Sent",
                text: "Thank you! We will contact you shortly."
            });

            // ✅ WhatsApp Auto Lead
            const whatsappText = `
New Enquiry – Sujaramaha Enterprises

Name: ${name}
Mobile: ${tel}
Email: ${email}
Message: ${message}
            `;
            window.open(
                `https://wa.me/919448344445?text=${encodeURIComponent(whatsappText)}`,
                "_blank"
            );

            form.reset();
            setResult("");
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Submission Failed",
                text: "Please try again later."
            });
            setResult("");
        }
    };

    return (
        <div className="contact-page">

            {/* HERO */}
            <section className="contact-hero">
                <div className="contact-hero-overlay">
                    <h1>Contact Sujaramaha Enterprises</h1>
                    <div className="services-line">
                        <span>Computer & Printers</span>
                        <span>Insurance</span>
                        <span>CCTV</span>
                        <span>Solar & Water Filter</span>
                        <span>Inverter & Battery</span>
                    </div>
                </div>
            </section>

            <section className="contact-container">

                {/* LEFT */}
                <div className="contact-info-heading">
                    <h2>We’d love to hear from you!</h2>
                    <p>Please let us know what you are looking for.</p>

                    <div className="contact-info">
                        <h3>Get in Touch</h3>

                        <div className="info-item">
                            <Phone />
                            <div>
                                <h4>Phone</h4>
                                <p>
                                    <a href="tel:+919448344445">+91 94483 44445</a><br />
                                    <a href="tel:+919972465259">+91 99724 65259</a>
                                </p>
                            </div>
                        </div>

                        <div className="info-item">
                            <Mail />
                            <div>
                                <h4>Email</h4>
                                <p>
                                    <a href="mailto:info@sujaramahaenterprises.com">
                                        info@sujaramahaenterprises.com
                                    </a>
                                </p>
                            </div>
                        </div>

                        <div className="info-item">
                            <MapPin />
                            <div>
                                <h4>Office Address</h4>
                                <p>
                                    Sujaramaha Enterprises, Vadanakal, Pavagada Taluk,
                                    Tumkur Dist, Karnataka – 561202
                                </p>
                            </div>
                        </div>

                        <a
                            href="https://wa.me/919448344445"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whatsapp-btn"
                        >
                            <FaWhatsapp /> WhatsApp Support
                        </a>
                    </div>
                </div>

                {/* RIGHT FORM */}
                <div className="contact-form-card">
                    <h2>Send Your Enquiry</h2>

                    <form onSubmit={onSubmit} noValidate>

                        {/* 🛡️ Honeypot */}
                        <input
                            type="text"
                            name="company"
                            tabIndex="-1"
                            autoComplete="off"
                            style={{ display: "none" }}
                        />

                        {/* 🛡️ Web3Forms FREE spam-protection fields */}
                        <input
                            type="hidden"
                            name="from_name"
                            value="Sujaramaha Enterprises Website"
                        />
                        <input
                            type="hidden"
                            name="subject"
                            value="New Contact Enquiry – Sujaramaha Enterprises"
                        />
                        <input
                            type="hidden"
                            name="replyto"
                            value="email"
                        />

                        <input type="text" name="name" placeholder="Full Name" />
                        {errors.name && <small className="form-error">{errors.name}</small>}

                        <input type="tel" name="tel" placeholder="Mobile Number" />
                        {errors.tel && <small className="form-error">{errors.tel}</small>}

                        <input type="email" name="email" placeholder="Email Address" />
                        {errors.email && <small className="form-error">{errors.email}</small>}

                        <textarea
                            name="message"
                            placeholder="How can we help you?"
                            rows="4"
                        />
                        {errors.message && (
                            <small className="form-error">{errors.message}</small>
                        )}

                        <label className="terms-label">
                            <input type="checkbox" name="terms" />
                            <span>
                                I agree to the{" "}
                                <a href="/privacy-policy" target="_blank" rel="noreferrer">
                                    Privacy Policy
                                </a>
                            </span>
                        </label>
                        {errors.terms && (
                            <small className="form-error">{errors.terms}</small>
                        )}

                        {/* reCAPTCHA kept (future Pro-ready) */}
                        <div
                            className="g-recaptcha"
                            data-sitekey="6LeXdlEsAAAAAA6WuToraMLYA6mUH78PV8E7LFkj"
                        ></div>

                        <button type="submit">Submit Enquiry</button>

                        {result && <span aria-live="polite">{result}</span>}
                    </form>
                </div>
            </section>

            {/* MAP */}
            <section className="map-section">
                <iframe
                    title="Sujaramaha Enterprises Location"
                    src="https://www.google.com/maps?q=Sujaramaha%20Enterprises%20Pavagada%20Karnataka&output=embed"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    allowFullScreen
                />
            </section>
        </div>
    );
};

export default Contact;
